import { AppointmentDto } from "../../api/appointmentApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";
import {
  getInstrumentIcon,
  getTranslations,
  useSupportedInstruments,
} from "../../api/instrumentApi.ts";
import { Calendar, Clock, MapPin, Repeat } from "lucide-react";
import { Link } from "react-router";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { format } from "date-fns";
import { formatSmartDate, formatRelativeTime } from "./formatBooking.ts";

interface AppointmentCardProps {
  appointment: AppointmentDto;
  /** Whether to show the relative time badge (e.g. "in 3 hours") */
  showRelative?: boolean;
}

export default function AppointmentCard({
  appointment,
  showRelative = false,
}: AppointmentCardProps) {
  const { language, dateFnsLocale } = useLanguageStore();
  const { data: supportedInstruments } = useSupportedInstruments();

  const InstrumentIcon = getInstrumentIcon(appointment.instrument);
  const instrumentLabel = getTranslations(
    supportedInstruments,
    appointment.instrument,
    language,
  );

  const fromDate = new Date(appointment.from);
  const toDate = new Date(appointment.to);

  const dateLabel = formatSmartDate(
    fromDate,
    dateFnsLocale,
    t`Today`,
    t`Tomorrow`,
  );
  const timeRange = `${format(fromDate, "HH:mm", { locale: dateFnsLocale })} – ${format(toDate, "HH:mm", { locale: dateFnsLocale })}`;
  const relativeTime = showRelative
    ? formatRelativeTime(fromDate, dateFnsLocale)
    : null;

  const isSubscription = appointment.source === "SUBSCRIPTION";

  return (
    <div className="border-soluud-primary bg-soluud-background-card flex flex-col gap-2 rounded-xl border-l-4 px-4 py-3 shadow-[--shadow-card] transition-shadow duration-200 hover:shadow-[--shadow-card-hover] sm:flex-row sm:items-center sm:gap-4">
      {/* Date & time block */}
      <div className="flex items-center gap-3 sm:w-44 sm:shrink-0 sm:flex-col sm:items-start sm:gap-0.5">
        <span className="text-soluud-text-primary text-sm font-semibold">
          {dateLabel}
        </span>
        <div className="text-soluud-text-secondary flex items-center gap-1.5 text-xs">
          <Clock className="h-3.5 w-3.5 shrink-0 opacity-60" />
          <span>{timeRange}</span>
        </div>
      </div>

      {/* Divider — desktop only */}
      <div className="bg-soluud-border hidden h-8 w-px shrink-0 sm:block" />

      {/* Details: instrument, person, location */}
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs leading-tight">
        <span className="text-soluud-text-secondary flex items-center gap-1.5">
          <InstrumentIcon className="text-soluud-primary h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{instrumentLabel}</span>
        </span>
        <span className="text-soluud-text-tertiary hidden sm:inline">·</span>
        <Link
          to={`/profile/${appointment.personId}`}
          className="text-soluud-text-secondary hover:text-soluud-primary truncate hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {appointment.personName}
        </Link>
        <span className="text-soluud-text-tertiary hidden sm:inline">·</span>
        <span className="text-soluud-text-secondary flex items-center gap-1 truncate">
          <MapPin className="h-3 w-3 shrink-0 opacity-60" />
          {appointment.locationName}
        </span>
      </div>

      {/* Source type badge */}
      {isSubscription ? (
        <span className="flex w-fit shrink-0 items-center gap-1 rounded-full bg-purple-100 px-2.5 py-0.5 text-[11px] font-medium text-purple-700">
          <Repeat className="h-3 w-3" />
          <Trans>Subscription</Trans>
        </span>
      ) : (
        <span className="flex w-fit shrink-0 items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-[11px] font-medium text-blue-700">
          <Calendar className="h-3 w-3" />
          <Trans>Single</Trans>
        </span>
      )}

      {/* Relative time badge */}
      {relativeTime && (
        <span className="bg-soluud-primary/10 text-soluud-primary w-fit shrink-0 rounded-full px-2.5 py-0.5 text-[11px] leading-tight font-medium capitalize sm:ml-auto">
          {relativeTime}
        </span>
      )}
    </div>
  );
}
